import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CoreLayout from "../../components/layout/CoreLayout";
import PageContainer from "../../components/PageContainer";
import PageHeader from "../../components/PageHeader";
import { loadOverlays, removeOverlay, saveOverlay } from "../../lib/actionItemOverlays";
import {
  type ActionItem,
  type ActionItemStatus,
  ActionItemsHttpError,
  fetchActionItems,
} from "../../lib/actionItems";
import { handleAuthHttpStatus, isAuthHttpStatus } from "../../lib/authErrors";
import { cardBorderClassName } from "../../lib/cardStyles";
import { formatDateTime } from "../../lib/formatDateTime";
import { formatPhoneDisplay } from "../../lib/formatPhone";
import { loadTeamMembers, memberInitials, type TeamMember } from "../../lib/teamMembers";
import { usePageTitle } from "../../lib/usePageTitle";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

function getCustomerId(): string | null {
  return localStorage.getItem("customerId");
}

const FILTER_LABELS: Record<ActionItemStatus | "all", string> = {
  all: "All",
  open: "Open",
  done: "Done",
  dismissed: "Dismissed",
};

const EMPTY_COPY: Record<ActionItemStatus | "all", string> = {
  all: "No follow-ups yet. Items appear here when your receptionist notes something to do after a call.",
  open: "Nothing open. You're caught up.",
  done: "No done items yet.",
  dismissed: "No dismissed items.",
};

function callerInitial(name: string | null): string {
  if (!name?.trim()) return "?";
  return name.trim().charAt(0).toUpperCase();
}

function IconActionButton({
  icon,
  label,
  onClick,
  hoverClassName,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  hoverClassName: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={`h-6 w-6 inline-flex items-center justify-center rounded-md text-gray-400 transition-colors cursor-pointer ${hoverClassName}`}
    >
      <i className={`${icon} text-sm`} aria-hidden />
    </button>
  );
}

function TextActionButton({
  icon,
  label,
  onClick,
  hoverTextClassName = "hover:text-white",
}: {
  icon: string;
  label: string;
  onClick: () => void;
  hoverTextClassName?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-300 ${hoverTextClassName} hover:bg-white/5 cursor-pointer`}
    >
      <i className={icon} aria-hidden />
      {label}
    </button>
  );
}

export default function OpenItems() {
  usePageTitle("Open Items");
  const navigate = useNavigate();
  const [items, setItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [saveError, setSaveError] = useState(false);
  const [activeFilter, setActiveFilter] = useState<ActionItemStatus | "all">("open");
  const [assignDropdownId, setAssignDropdownId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const assignMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const customerId = getCustomerId();
    if (!customerId) {
      setLoading(false);
      setLoadError(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fetched = await fetchActionItems(customerId);
        const overlays = loadOverlays();
        const merged: ActionItem[] = fetched.map((base) => {
          // Overlays cover status/completed_at only — assignee is now backend-persisted
          const ov = overlays[base.id];
          if (!ov) return base;
          return {
            ...base,
            ...(ov.status !== undefined ? { status: ov.status } : {}),
            ...(ov.completed_at !== undefined ? { completed_at: ov.completed_at } : {}),
          };
        });
        if (cancelled) return;
        setItems(merged);
        localStorage.setItem("ns_action_items_cache", JSON.stringify(fetched));
      } catch (e) {
        if (e instanceof ActionItemsHttpError && isAuthHttpStatus(e.status)) {
          void handleAuthHttpStatus(e.status, () => {
            setLoadError(true);
          });
          return;
        }
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  useEffect(() => {
    setTeamMembers(loadTeamMembers());
  }, []);

  useEffect(() => {
    if (!assignDropdownId) return;
    const onMouseDown = (e: MouseEvent) => {
      if (assignMenuRef.current?.contains(e.target as Node)) return;
      setAssignDropdownId(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAssignDropdownId(null);
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [assignDropdownId]);

  const counts = useMemo(
    () => ({
      open: items.filter((i) => i.status === "open").length,
      done: items.filter((i) => i.status === "done").length,
      dismissed: items.filter((i) => i.status === "dismissed").length,
      all: items.length,
    }),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (activeFilter === "all") return items;
    return items.filter((i) => i.status === activeFilter);
  }, [items, activeFilter]);

  const persistStatus = async (
    id: string,
    status: "done" | "dismissed",
    previous: Pick<ActionItem, "status" | "completed_at">
  ) => {
    try {
      const res = await fetch(`${API_BASE}/action-items/${id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json().catch(() => null);
      const nextStatus = updated?.status;
      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? {
                ...i,
                ...(nextStatus === "open" || nextStatus === "done" || nextStatus === "dismissed"
                  ? { status: nextStatus as ActionItemStatus }
                  : {}),
                ...(updated && "completed_at" in updated
                  ? { completed_at: updated.completed_at ?? null }
                  : {}),
              }
            : i
        )
      );
      removeOverlay(id);
    } catch {
      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? { ...i, status: previous.status, completed_at: previous.completed_at }
            : i
        )
      );
      setSaveError(true);
    }
  };

  const markDone = (id: string) => {
    setSaveError(false);
    const previous = items.find((i) => i.id === id);
    if (!previous) return;
    const completedAt = new Date().toISOString();
    setItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, status: "done", completed_at: completedAt } : i
      )
    );
    void persistStatus(id, "done", previous);
  };

  const dismiss = (id: string) => {
    setSaveError(false);
    const previous = items.find((i) => i.id === id);
    if (!previous) return;
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, status: "dismissed" } : i))
    );
    void persistStatus(id, "dismissed", previous);
  };

  const reopen = (id: string) => {
    // TODO: backend PATCH /action-items only accepts status "done" or "dismissed", so reopen stays local via saveOverlay until it accepts "open"
    setSaveError(false);
    setItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, status: "open", completed_at: null } : i
      )
    );
    saveOverlay(id, { status: "open", completed_at: null });
  };

  const openAssignDropdown = (itemId: string) => {
    setSaveError(false);
    setTeamMembers(loadTeamMembers());
    setAssignDropdownId((prev) => (prev === itemId ? null : itemId));
  };

  const assignItem = async (itemId: string, member: TeamMember) => {
    setSaveError(false);
    // Optimistic update
    setItems((prev) =>
      prev.map((i) =>
        i.id === itemId
          ? { ...i, assignee_id: member.id, assignee_name: member.name }
          : i
      )
    );
    setAssignDropdownId(null);
    try {
      const res = await fetch(`${API_BASE}/action-items/${itemId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignee_id: member.id, assignee_name: member.name }),
      });
      if (res.ok) {
        const updated = await res.json();
        setItems((prev) =>
          prev.map((i) =>
            i.id === itemId
              ? {
                  ...i,
                  assignee_id: updated.assignee_id ?? member.id,
                  assignee_name: updated.assignee_name ?? member.name,
                }
              : i
          )
        );
      } else {
        // Revert optimistic update on failure
        setItems((prev) =>
          prev.map((i) =>
            i.id === itemId ? { ...i, assignee_id: null, assignee_name: null } : i
          )
        );
      }
    } catch {
      // Revert on network error
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, assignee_id: null, assignee_name: null } : i
        )
      );
    }
  };

  const unassignItem = async (itemId: string) => {
    setSaveError(false);
    const previous = items.find((i) => i.id === itemId);
    // Optimistic update
    setItems((prev) =>
      prev.map((i) =>
        i.id === itemId ? { ...i, assignee_id: null, assignee_name: null } : i
      )
    );
    try {
      const res = await fetch(`${API_BASE}/action-items/${itemId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assignee_id: null, assignee_name: null }),
      });
      if (!res.ok && previous) {
        // Revert optimistic update on failure
        setItems((prev) =>
          prev.map((i) =>
            i.id === itemId
              ? { ...i, assignee_id: previous.assignee_id, assignee_name: previous.assignee_name }
              : i
          )
        );
      }
    } catch {
      if (previous) {
        // Revert on network error
        setItems((prev) =>
          prev.map((i) =>
            i.id === itemId
              ? { ...i, assignee_id: previous.assignee_id, assignee_name: previous.assignee_name }
              : i
          )
        );
      }
    }
  };

  return (
    <CoreLayout>
      <PageContainer>
        <PageHeader
          title="Open Items"
          subtitle="Follow-ups and action items from every call."
        />

        <div className="space-y-6">
          {/* Stats row */}
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              {(
                [
                  { label: "Open", value: counts.open, variant: "open" as const },
                  { label: "Done", value: counts.done, variant: "muted" as const },
                  { label: "Dismissed", value: counts.dismissed, variant: "muted" as const },
                ] as const
              ).map((stat) => (
                <div
                  key={stat.label}
                  className={`rounded-lg p-4 ${
                    stat.variant === "open"
                      ? "border border-[#ff6a00]/30 bg-[#ff6a00]/5"
                      : "border border-white/10 bg-transparent"
                  }`}
                >
                  <span
                    className="text-xs text-gray-500 uppercase tracking-wide"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {stat.label}
                  </span>
                  <p
                    className={`text-2xl font-semibold mt-1 ${
                      stat.variant === "open" ? "text-[#ff6a00]" : "text-gray-300"
                    }`}
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            {saveError && (
              <p className="text-sm text-red-300">Couldn&apos;t save. Try again.</p>
            )}
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2">
            {(["all", "open", "done", "dismissed"] as const).map((filter) => {
              const isActive = activeFilter === filter;
              const count = counts[filter];
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setActiveFilter(filter)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer"
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    background: isActive
                      ? "linear-gradient(90deg, #8a2be2, #ff6a00)"
                      : "rgba(255,255,255,0.05)",
                    color: isActive ? "#fff" : "#aaaaaa",
                    border: isActive
                      ? "1px solid transparent"
                      : "1px solid rgba(138,43,226,0.2)",
                  }}
                >
                  {FILTER_LABELS[filter]}
                  {count > 0 && (
                    <span
                      className="text-xs px-1.5 py-0.5 rounded-full"
                      style={{
                        background: isActive ? "rgba(255,255,255,0.2)" : "rgba(138,43,226,0.2)",
                        color: isActive ? "#fff" : "#c084fc",
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Items list */}
          {loadError ? (
            <div className="text-center py-16">
              <p
                className="text-red-300 text-sm"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Couldn&apos;t load action items
              </p>
              <button
                type="button"
                onClick={() => setLoadAttempt((n) => n + 1)}
                className="mt-3 px-4 py-2 rounded-lg text-sm font-semibold text-white cursor-pointer"
                style={{ background: "linear-gradient(90deg, #8a2be2, #ff6a00)" }}
              >
                Retry
              </button>
            </div>
          ) : loading ? (
            <div className="text-center py-16">
              <p
                className="text-gray-400 text-sm"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Loading…
              </p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-checkbox-circle-line text-5xl text-purple-400/30 mb-5 block" />
              <p
                className="text-gray-400"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {EMPTY_COPY[activeFilter]}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item) => {
                const callWhen = formatDateTime(item.created_at);
                const closedWhen =
                  item.status === "open" ? "" : formatDateTime(item.completed_at);
                const phoneDisplay = formatPhoneDisplay(item.caller_phone);
                const sourceCallId = item.source_call_id?.trim() ?? "";
                return (
                <div
                  key={item.id}
                  className={`rounded-xl p-4 ${cardBorderClassName}`}
                  style={{
                    background: "rgba(28, 28, 37, 0.8)",
                  }}
                >
                  <div className="flex gap-4">
                    <div
                      className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white"
                      style={{ background: "linear-gradient(135deg, #8a2be2, #ff6a00)" }}
                    >
                      {callerInitial(item.caller_name)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="mb-2">
                        <p
                          className="text-white font-semibold text-sm"
                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                          {item.caller_name || "Unknown caller"}
                        </p>
                        {phoneDisplay && (
                          <p className="text-xs font-mono text-orange-400">
                            {phoneDisplay}
                          </p>
                        )}
                      </div>

                      <p
                        className="text-gray-400 text-sm leading-relaxed mb-1"
                        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                      >
                        {item.text}
                      </p>

                      <div className="flex items-center justify-between gap-4 flex-wrap mt-3">
                        {(callWhen || closedWhen) && (
                          <div>
                            {callWhen && (
                              <span
                                className="text-xs text-gray-500"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                Call on {callWhen}
                              </span>
                            )}
                            {closedWhen && (
                              <span
                                className="text-xs text-gray-500"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                {callWhen ? " · " : ""}
                                {item.status === "done" ? "Done" : "Dismissed"} {closedWhen}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-1 flex-wrap">
                          {item.status !== "open" && item.assignee_name && (
                            <div
                              className="flex items-center gap-2 px-2 py-1 rounded-lg"
                              style={{
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.1)",
                              }}
                            >
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-gray-300 bg-white/10">
                                {memberInitials(item.assignee_name)}
                              </div>
                              <span
                                className="text-xs text-gray-400 font-medium"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                {item.assignee_name}
                              </span>
                            </div>
                          )}

                          {item.status === "open" && (
                            <>
                              {item.assignee_id && item.assignee_name ? (
                                <div className="flex items-center gap-2 px-2 py-1 rounded-lg"
                                  style={{
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(138, 43, 226, 0.2)",
                                  }}
                                >
                                  <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                    style={{ background: "linear-gradient(135deg, #8a2be2, #ff6a00)" }}
                                  >
                                    {memberInitials(item.assignee_name)}
                                  </div>
                                  <span
                                    className="text-xs text-white font-medium"
                                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                  >
                                    {item.assignee_name}
                                  </span>
                                  <IconActionButton
                                    icon="ri-close-line"
                                    label="Unassign"
                                    onClick={() => unassignItem(item.id)}
                                    hoverClassName="hover:text-gray-300"
                                  />
                                </div>
                              ) : (
                                <div
                                  className="relative"
                                  ref={assignDropdownId === item.id ? assignMenuRef : undefined}
                                >
                                  <TextActionButton
                                    icon="ri-user-add-line"
                                    label="Assign"
                                    onClick={() => openAssignDropdown(item.id)}
                                  />
                                  {assignDropdownId === item.id && (
                                    <div
                                      className="absolute right-0 top-full z-20 mt-1 min-w-[200px] rounded-lg py-1 shadow-lg"
                                      style={{
                                        background: "rgba(28, 28, 37, 0.95)",
                                        border: "1px solid rgba(138, 43, 226, 0.2)",
                                      }}
                                    >
                                      {teamMembers.length === 0 ? (
                                        <p
                                          className="px-3 py-2 text-xs text-gray-500"
                                          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                        >
                                          No team members — add them in the Team tab.
                                        </p>
                                      ) : (
                                        teamMembers.map((member) => (
                                          <button
                                            key={member.id}
                                            type="button"
                                            onClick={() => assignItem(item.id, member)}
                                            className="w-full px-3 py-2 text-left text-xs text-gray-300 hover:bg-white/5 cursor-pointer"
                                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                          >
                                            {member.name}
                                          </button>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              <TextActionButton
                                icon="ri-check-line"
                                label="Done"
                                onClick={() => markDone(item.id)}
                                hoverTextClassName="hover:text-green-400"
                              />
                              <TextActionButton
                                icon="ri-close-line"
                                label="Dismiss"
                                onClick={() => dismiss(item.id)}
                                hoverTextClassName="hover:text-red-400"
                              />
                            </>
                          )}

                          {item.status === "done" && (
                            <>
                              <span
                                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                                style={{
                                  background: "rgba(34,197,94,0.15)",
                                  color: "#86efac",
                                  border: "1px solid rgba(34,197,94,0.3)",
                                  fontFamily: "'Space Grotesk', sans-serif",
                                }}
                              >
                                ✓ Done
                              </span>
                              <button
                                type="button"
                                onClick={() => reopen(item.id)}
                                title="Reopen is saved in this browser only until the backend supports it"
                                className="text-xs text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                Reopen
                              </button>
                            </>
                          )}

                          {item.status === "dismissed" && (
                            <>
                              <span
                                className="text-xs font-semibold px-2.5 py-1 rounded-full"
                                style={{
                                  background: "rgba(107,114,128,0.15)",
                                  color: "#9ca3af",
                                  border: "1px solid rgba(107,114,128,0.3)",
                                  fontFamily: "'Space Grotesk', sans-serif",
                                }}
                              >
                                Dismissed
                              </span>
                              <button
                                type="button"
                                onClick={() => reopen(item.id)}
                                title="Reopen is saved in this browser only until the backend supports it"
                                className="text-xs text-gray-500 hover:text-gray-300 transition-colors cursor-pointer"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                Reopen
                              </button>
                            </>
                          )}

                          {sourceCallId && (
                            <TextActionButton
                              icon="ri-phone-line"
                              label="View call"
                              onClick={() =>
                                navigate(`/call-logs?sid=${encodeURIComponent(sourceCallId)}`)
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
      </PageContainer>
    </CoreLayout>
  );
}
